import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEvent } from '../hooks/useEvent';
import { useAuth } from '../auth/AuthContext';
import { scoresApi } from '../api/scores';
import { sidegamesApi } from '../api/sidegames';
import { Button } from '../components/Button';
import { HoleStepper } from '../components/HoleStepper';
import { Modal } from '../components/Modal';
import { ApiError } from '../api/client';

type ClaimKind = 'kp' | 'ld' | 'hio';

type PendingClaim = {
  kind: ClaimKind;
  holeNumber: number;
  pickedParticipantId: string;
};

const KIND_LABEL: Record<ClaimKind, string> = {
  kp: 'Closest to Pin',
  ld: 'Longest Drive',
  hio: 'Hole-in-One',
};

export function EventScoreEntry() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const adminAsTeamId = searchParams.get('as');
  const { data: eventDetail, isLoading } = useEvent(id);
  const { actor } = useAuth();
  const qc = useQueryClient();

  const scoresQ = useQuery({
    queryKey: ['scores', id],
    queryFn: () => scoresApi.list(id!),
    enabled: !!id,
    refetchOnWindowFocus: true,
  });

  const leaderboardQ = useQuery({
    queryKey: ['leaderboard', id],
    queryFn: () => scoresApi.leaderboard(id!),
    enabled: !!id,
    refetchOnWindowFocus: true,
  });

  const [pending, setPending] = useState<Record<number, number>>({});
  const [err, setErr] = useState<string | null>(null);
  const [claimPrompt, setClaimPrompt] = useState<PendingClaim | null>(null);
  const [submitOpen, setSubmitOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const timers = useRef<Record<number, ReturnType<typeof setTimeout>>>({});

  const subject = useMemo(() => {
    if (!eventDetail) return null;
    const { event, participants, teams } = eventDetail;
    const isAdmin = actor?.kind === 'user' && event.admin_user_id === actor.user.id;

    // Admin override: ?as=<teamId> scores for any team in a Best Ball event.
    if (adminAsTeamId && isAdmin && event.format === 'best_ball_4some') {
      const team = teams.find((t) => t.id === adminAsTeamId);
      if (team) {
        const members = participants.filter((p) => p.teamId === team.id);
        return {
          kind: 'team' as const,
          id: team.id,
          myParticipantId: members[0]?.id ?? '',
          adminOverride: true,
          overrideName: team.name,
        };
      }
    }

    const me = participants.find(
      (p) =>
        (actor?.kind === 'user' && p.userId === actor.user.id) ||
        (actor?.kind === 'guest' && p.guestId === actor.guest.id),
    );
    if (event.format === 'best_ball_4some') {
      if (!me?.teamId) return null;
      return {
        kind: 'team' as const,
        id: me.teamId,
        myParticipantId: me.id,
        adminOverride: false,
        overrideName: null,
      };
    }
    if (!me) return null;
    return {
      kind: 'participant' as const,
      id: me.id,
      myParticipantId: me.id,
      adminOverride: false,
      overrideName: null,
    };
  }, [eventDetail, actor, adminAsTeamId]);

  const scoresByHole = useMemo(() => {
    const m = new Map<number, { strokes: number; enteredAt: string; enteredByUserId: string | null; enteredByGuestId: string | null }>();
    if (!scoresQ.data || !subject) return m;
    for (const s of scoresQ.data.scores) {
      if (subject.kind === 'team' && s.team_id === subject.id) {
        m.set(s.hole_number, { strokes: s.strokes, enteredAt: s.entered_at, enteredByUserId: s.entered_by_user_id, enteredByGuestId: s.entered_by_guest_id });
      }
      if (subject.kind === 'participant' && s.participant_id === subject.id) {
        m.set(s.hole_number, { strokes: s.strokes, enteredAt: s.entered_at, enteredByUserId: s.entered_by_user_id, enteredByGuestId: s.entered_by_guest_id });
      }
    }
    return m;
  }, [scoresQ.data, subject]);

  useEffect(() => {
    return () => {
      Object.values(timers.current).forEach(clearTimeout);
    };
  }, []);

  if (isLoading || !eventDetail) return <div className="container muted">Loading…</div>;

  const { event, holes, participants } = eventDetail;

  if (event.status !== 'active') {
    return (
      <div className="container">
        <div className="card stack">
          <h2>Scoring is not open</h2>
          <p className="muted">
            {event.status === 'draft'
              ? 'The admin has not started this event yet.'
              : 'This event has been finalized — scores are locked.'}
          </p>
          <Link to={`/e/${event.id}`}><Button variant="secondary" block>Back to lobby</Button></Link>
        </div>
      </div>
    );
  }

  if (!subject) {
    return (
      <div className="container">
        <div className="card stack">
          <h2>Pick a team or join the event</h2>
          <p className="muted">You need to be on a team (best ball) or a participant (stroke play) to enter scores.</p>
          <Link to={`/e/${event.id}`}><Button variant="secondary" block>Back to lobby</Button></Link>
        </div>
      </div>
    );
  }

  const teamMembers =
    subject.kind === 'team' ? participants.filter((p) => p.teamId === subject.id) : [];
  const isBestBall = event.format === 'best_ball_4some';

  const mineParticipantIds = new Set<string>(
    isBestBall ? teamMembers.map((m) => m.id) : [subject.myParticipantId],
  );

  const lb = leaderboardQ.data;
  const isKpMine = new Map<number, boolean>();
  const isLdMine = new Map<number, boolean>();
  const isHioMine = new Map<number, boolean>();
  if (lb) {
    for (const c of lb.kp) {
      if (c.isCurrent && mineParticipantIds.has(c.participantId)) {
        isKpMine.set(c.holeNumber, true);
      }
    }
    for (const c of lb.ld) {
      if (c.isCurrent && mineParticipantIds.has(c.participantId)) {
        isLdMine.set(c.holeNumber, true);
      }
    }
    for (const c of lb.hio) {
      if (mineParticipantIds.has(c.participantId)) {
        isHioMine.set(c.holeNumber, true);
      }
    }
  }

  const mySubmitted =
    lb?.rows.find(
      (r) =>
        (subject.kind === 'team' && r.subjectKind === 'team' && r.subjectId === subject.id) ||
        (subject.kind === 'participant' &&
          r.subjectKind === 'participant' &&
          r.subjectId === subject.id),
    )?.submitted ?? false;

  const allHolesFilled = holes.every((h) => scoresByHole.has(h.holeNumber));

  const submitScorecard = async () => {
    setSubmitting(true);
    setErr(null);
    try {
      await scoresApi.submit(event.id);
      await Promise.all([scoresQ.refetch(), leaderboardQ.refetch()]);
      qc.invalidateQueries({ queryKey: ['leaderboard', event.id] });
      setSubmitOpen(false);
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Could not submit scorecard');
    } finally {
      setSubmitting(false);
    }
  };

  const persistScore = async (holeNumber: number, strokes: number) => {
    await scoresApi.put(event.id, {
      holeNumber,
      strokes,
      ...(subject.kind === 'team' ? { teamId: subject.id } : { participantId: subject.id }),
    });
    await scoresQ.refetch();
    qc.invalidateQueries({ queryKey: ['leaderboard', event.id] });
  };

  const saveScore = (holeNumber: number, strokes: number) => {
    setPending((p) => ({ ...p, [holeNumber]: strokes }));
    clearTimeout(timers.current[holeNumber]);
    timers.current[holeNumber] = setTimeout(async () => {
      try {
        setErr(null);
        await persistScore(holeNumber, strokes);
      } catch (e) {
        setErr(e instanceof ApiError ? e.message : 'Could not save score');
      }
    }, 400);
  };

  const openClaim = (kind: ClaimKind, holeNumber: number) => {
    const defaultPicked = isBestBall
      ? teamMembers[0]?.id ?? ''
      : subject.myParticipantId;
    setClaimPrompt({ kind, holeNumber, pickedParticipantId: defaultPicked });
  };

  const confirmClaim = async () => {
    if (!claimPrompt) return;
    const { kind, holeNumber, pickedParticipantId } = claimPrompt;
    if (isBestBall && !pickedParticipantId) {
      setErr('Pick a team member');
      return;
    }
    const pid = isBestBall ? pickedParticipantId : undefined;
    try {
      setErr(null);
      if (kind === 'kp') await sidegamesApi.claimKp(event.id, holeNumber, pid);
      else if (kind === 'ld') await sidegamesApi.claimLd(event.id, holeNumber, pid);
      else await sidegamesApi.claimHio(event.id, holeNumber, pid);
      qc.invalidateQueries({ queryKey: ['leaderboard', event.id] });
      await leaderboardQ.refetch();
      setClaimPrompt(null);
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Could not record claim');
    }
  };

  const totalStrokes = holes.reduce((acc, h) => acc + (scoresByHole.get(h.holeNumber)?.strokes ?? 0), 0);
  const totalPar = holes.reduce((acc, h) => acc + h.par, 0);
  const diff = totalStrokes - totalPar;

  return (
    <div className="container stack">
      <div className="card row" style={{ justifyContent: 'space-between' }}>
        <div>
          <h2 style={{ margin: 0 }}>Enter scores</h2>
          <div className="muted" style={{ fontSize: 13 }}>
            {subject.kind === 'team' ? `Team: ${eventDetail.teams.find((t) => t.id === subject.id)?.name ?? ''}` : 'Personal scoring'}
          </div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div className="muted" style={{ fontSize: 12 }}>Total</div>
          <div style={{ fontSize: 22, fontWeight: 700 }}>
            {totalStrokes}{' '}
            <span className="muted" style={{ fontSize: 16, fontWeight: 500 }}>
              {totalStrokes === 0 ? '' : diff === 0 ? 'E' : diff > 0 ? `+${diff}` : diff}
            </span>
          </div>
        </div>
      </div>

      {err && <div className="error">{err}</div>}

      {subject.adminOverride && (
        <div className="card" style={{ background: 'var(--color-yellow-500)' }}>
          <strong>Admin override.</strong>{' '}
          <span>You are entering scores on behalf of team <strong>{subject.overrideName}</strong>.</span>
        </div>
      )}

      {mySubmitted && (
        <div className="card" style={{ background: 'var(--color-green-100)' }}>
          <strong>Scorecard submitted.</strong>{' '}
          <span className="muted">Scores are locked. Ask the admin to unlock if you need to fix something.</span>
        </div>
      )}

      <Link to={`/e/${event.id}/leaderboard`}><Button variant="secondary" block>View leaderboard</Button></Link>

      <div className="stack">
        {holes.map((h) => {
          const current = pending[h.holeNumber] ?? scoresByHole.get(h.holeNumber)?.strokes ?? null;
          const kpMine = isKpMine.get(h.holeNumber) === true;
          const ldMine = isLdMine.get(h.holeNumber) === true;
          const hioMine = isHioMine.get(h.holeNumber) === true;
          return (
            <div key={h.holeNumber} className="stack" style={{ marginBottom: 4 }}>
              <HoleStepper
                holeNumber={h.holeNumber}
                par={h.par}
                strokes={current}
                onChange={(v) => saveScore(h.holeNumber, v)}
                disabled={mySubmitted}
                sideButtons={
                  <>
                    {h.isKp && (
                      <button
                        type="button"
                        className="sg-btn sg-btn-kp"
                        disabled={kpMine || mySubmitted}
                        onClick={() => openClaim('kp', h.holeNumber)}
                        title={kpMine ? 'You currently hold KP on this hole' : "I'm the KP"}
                      >
                        {kpMine ? 'KP ✓' : "I'm the KP"}
                      </button>
                    )}
                    {h.isLd && (
                      <button
                        type="button"
                        className="sg-btn sg-btn-ld"
                        disabled={ldMine || mySubmitted}
                        onClick={() => openClaim('ld', h.holeNumber)}
                        title={ldMine ? 'You currently hold LD on this hole' : "I'm the LD"}
                      >
                        {ldMine ? 'LD ✓' : "I'm the LD"}
                      </button>
                    )}
                    {h.isHio && (
                      <button
                        type="button"
                        className="sg-btn sg-btn-hio"
                        disabled={hioMine || mySubmitted}
                        onClick={() => openClaim('hio', h.holeNumber)}
                        title={hioMine ? 'Hole-in-One already recorded for your team on this hole' : 'Record a Hole-in-One on the prize hole'}
                      >
                        {hioMine ? 'HIO ✓' : 'Hole in One'}
                      </button>
                    )}
                  </>
                }
              />
            </div>
          );
        })}
      </div>

      {!mySubmitted && !subject.adminOverride && (
        <Button
          variant="accent"
          block
          disabled={!allHolesFilled || submitting}
          onClick={() => setSubmitOpen(true)}
        >
          {allHolesFilled ? 'Submit scorecard' : `Submit scorecard (${scoresByHole.size}/${holes.length} holes)`}
        </Button>
      )}

      <Modal
        open={submitOpen}
        title="Submit scorecard?"
        body={
          <p style={{ margin: 0 }}>
            Once submitted, scores are locked for everyone on{' '}
            {subject.kind === 'team' ? 'your team' : 'your card'}. Only the admin can unlock.
            Continue?
          </p>
        }
        actions={
          <>
            <Button variant="secondary" onClick={() => setSubmitOpen(false)} disabled={submitting}>
              Cancel
            </Button>
            <Button onClick={submitScorecard} disabled={submitting}>
              {submitting ? 'Submitting…' : 'Submit'}
            </Button>
          </>
        }
        onClose={() => setSubmitOpen(false)}
      />

      <Modal
        open={!!claimPrompt}
        title={
          claimPrompt
            ? `${KIND_LABEL[claimPrompt.kind]} on hole ${claimPrompt.holeNumber}`
            : ''
        }
        body={
          claimPrompt ? (
            isBestBall ? (
              <div className="stack">
                <p style={{ margin: 0 }}>
                  Pick the team member who made the {KIND_LABEL[claimPrompt.kind]}.
                </p>
                <select
                  value={claimPrompt.pickedParticipantId}
                  onChange={(e) =>
                    setClaimPrompt((prev) =>
                      prev ? { ...prev, pickedParticipantId: e.target.value } : prev,
                    )
                  }
                  aria-label={`${KIND_LABEL[claimPrompt.kind]} player`}
                >
                  {teamMembers.map((m) => (
                    <option key={m.id} value={m.id}>{m.displayName}</option>
                  ))}
                </select>
              </div>
            ) : (
              <p style={{ margin: 0 }}>
                Confirm that you made the {KIND_LABEL[claimPrompt.kind]}?
              </p>
            )
          ) : null
        }
        actions={
          <>
            <Button variant="secondary" onClick={() => setClaimPrompt(null)}>Cancel</Button>
            <Button onClick={confirmClaim}>Confirm</Button>
          </>
        }
        onClose={() => setClaimPrompt(null)}
      />
    </div>
  );
}
